import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Check, CreditCard, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { API } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const STATE_COPY = {
  trialing: { tone: 'bg-teal-50 border-teal-200 text-teal-800', label: 'Free trial' },
  active: { tone: 'bg-teal-50 border-teal-200 text-teal-800', label: 'Active' },
  trial_expired: { tone: 'bg-red-50 border-red-200 text-red-800', label: 'Trial ended' },
  past_due: { tone: 'bg-amber-50 border-amber-200 text-amber-800', label: 'Payment failed' },
  cancelled: { tone: 'bg-slate-100 border-slate-200 text-slate-700', label: 'Cancelled' },
  suspended: { tone: 'bg-red-50 border-red-200 text-red-800', label: 'Suspended' },
};

const PaymentLinkCard = ({ state }) => {
  const { authHeader } = useAuth();
  const [link, setLink] = useState(null);
  useEffect(() => {
    axios.get(`${API}/subscription/payment-link`, { headers: authHeader() }).then((r) => setLink(r.data.link)).catch(() => {});
  }, [authHeader]);
  if (link) {
    return (
      <div className="rounded-2xl border border-teal-300 bg-teal-50/70 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="subscription-payment-link">
        <div className="text-sm text-teal-900"><b>Your payment link is ready.</b> {link.purpose} · {money(link.amount)}</div>
        <a href={link.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-xl" data-testid="subscription-pay-link-btn"><CreditCard size={16} /> Pay {money(link.amount)}</a>
      </div>
    );
  }
  if (['trial_expired', 'past_due', 'cancelled'].includes(state)) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="subscription-await-link">
        Your free trial has ended. Unisol will send you a payment link for your plan shortly. Your data is safe and you can still view everything; new entries are paused until payment.
      </div>
    );
  }
  return null;
};

const SubscriptionPage = () => {
  const { authHeader } = useAuth();
  const { refresh, moduleInfo } = useTenant();
  const [state, setState] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [busyPlan, setBusyPlan] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/state`, { headers: authHeader() });
      setState(data);
      setCycle(data.billing_cycle || 'monthly');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const choose = async (planKey, cycleOverride) => {
    setNotice('');
    setBusyPlan(planKey);
    try {
      await axios.post(
        `${API}/subscription/plan`,
        { plan: planKey, billing_cycle: cycleOverride || cycle },
        { headers: authHeader() },
      );
      setNotice('Plan updated. No payment is needed now.');
      await load();
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setNotice(typeof d === 'string' ? d : (d?.detail || 'Could not change the plan. Please try again.'));
    } finally {
      setBusyPlan('');
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Loading your plan…</div>;
  }
  if (!state) return <div className="p-8 text-slate-500">No subscription information available.</div>;

  const acc = state.access || {};
  const copy = STATE_COPY[acc.state] || STATE_COPY.active;

  // Nudge anyone still on monthly towards annual, while there is a decision to
  // make — a trial running out, or a lapsed/failed subscription being restarted.
  const nudgeStates = ['trialing', 'trial_expired', 'past_due', 'cancelled'];
  const currentPlan = (state.plans || []).find((p) => p.key === state.plan);
  // A workshop on a negotiated discount pays less than the list price, so
  // every figure on this page is quoted net of it — a nudge that cites prices
  // they will never be charged is worse than no nudge.
  const off = Number(state.discount?.percent || 0);
  const net = (amount) => (off > 0
    ? Math.max(1, Math.round((Number(amount) || 0) * (100 - off) / 100))
    : Number(amount) || 0);
  const upsell = state.billing_cycle !== 'yearly'
    && nudgeStates.includes(acc.state)
    && currentPlan
    && currentPlan.price_yearly < currentPlan.price_monthly * 12
    ? {
      plan: currentPlan,
      monthlyYear: net(currentPlan.price_monthly) * 12,
      yearly: net(currentPlan.price_yearly),
      saving: net(currentPlan.price_monthly) * 12 - net(currentPlan.price_yearly),
    }
    : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your Driveline plan</h1>
        <p className="text-sm text-slate-500 mt-1">Choose your plan and see what you will pay. No auto-debit, no card needed.</p>
      </div>

      <div className={`rounded-2xl border p-5 ${copy.tone}`} data-testid="subscription-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-70">{copy.label}</div>
            <div className="text-xl font-semibold mt-0.5">
              {acc.plan_name} · {money(state.amount_inr)}/{state.billing_cycle === 'yearly' ? 'year' : 'month'}
              {state.discount?.percent > 0 && (
                <span className="ml-2 text-sm font-normal text-slate-400 line-through">
                  {money(state.list_amount_inr)}
                </span>
              )}
            </div>
            {state.discount?.percent > 0 && (
              <div className="text-xs text-emerald-700 mt-1" data-testid="subscription-discount">
                {state.discount.percent}% off{state.discount.note ? ` · ${state.discount.note}` : ''}
                {state.discount.until ? ` · until ${state.discount.until}` : ''}
                {state.discount.saves ? ` — you save ${money(state.discount.saves)} each ${state.billing_cycle === 'yearly' ? 'year' : 'month'}` : ''}
              </div>
            )}
            {acc.state === 'trialing' && (
              <div className="text-sm mt-1" data-testid="subscription-days-left">
                {acc.days_left === 0 ? 'Your trial ends today.' : `${acc.days_left} days left in your free trial.`}
              </div>
            )}
            {acc.reason && <div className="text-sm mt-1">{acc.reason}</div>}
          </div>
          {acc.read_only && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/70" data-testid="subscription-readonly-chip">
              READ ONLY — new entries paused
            </span>
          )}
        </div>
      </div>

      <PaymentLinkCard state={acc.state} />

      {upsell && (
        <div
          className="rounded-2xl border border-teal-300 bg-teal-50/70 p-5 flex flex-wrap items-center justify-between gap-4"
          data-testid="subscription-annual-nudge"
        >
          <div>
            <div className="flex items-center gap-2 text-teal-900 font-semibold">
              <Sparkles size={17} className="text-teal-600" />
              Pay yearly and keep {money(upsell.saving)}
            </div>
            <p className="text-sm text-teal-900/80 mt-1" data-testid="subscription-annual-nudge-copy">
              {upsell.plan.name} is {money(upsell.monthlyYear)} over a year on monthly billing,
              or {money(upsell.yearly)} paid annually — two months free.
              {off > 0 && <> Both figures already include your {off}% discount.</>}
              {acc.state === 'trialing' && acc.days_left != null && (
                <> You have {acc.days_left === 0 ? 'until the end of today' : `${acc.days_left} days`} to decide.</>
              )}
            </p>
          </div>
          <button
            onClick={() => { setCycle('yearly'); choose(upsell.plan.key, 'yearly'); }}
            disabled={Boolean(busyPlan)}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors shrink-0"
            data-testid="subscription-annual-nudge-cta"
          >
            {busyPlan === upsell.plan.key ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
            Switch to yearly
          </button>
        </div>
      )}

      <div className="flex items-center gap-2" data-testid="subscription-cycle-toggle">
        {['monthly', 'yearly'].map((c) => (
          <button
            key={c}
            onClick={() => setCycle(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              cycle === c ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'}`}
            data-testid={`subscription-cycle-${c}`}
          >
            {c === 'monthly' ? 'Monthly' : 'Yearly · 2 months free'}
          </button>
        ))}
      </div>

      {notice && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3" data-testid="subscription-notice">
          {notice}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4" data-testid="subscription-plans">
        {(state.plans || []).map((p) => {
          const isCurrent = p.key === state.plan;
          const listPrice = cycle === 'yearly' ? p.price_yearly : p.price_monthly;
          const price = net(listPrice);
          return (
            <div
              key={p.key}
              className={`rounded-2xl border bg-white p-5 flex flex-col ${
                isCurrent ? 'border-slate-900 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.45)]' : 'border-slate-200'}`}
              data-testid={`subscription-plan-${p.key}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold">{p.name}</span>
                {isCurrent && <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 rounded-full">Current</span>}
              </div>
              <div className="mt-2 text-2xl font-bold tabular-nums">{money(price)}
                <span className="text-sm font-normal text-slate-400">/{cycle === 'yearly' ? 'yr' : 'mo'}</span>
                {off > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400 line-through" data-testid={`subscription-list-${p.key}`}>
                    {money(listPrice)}
                  </span>
                )}
              </div>
              {cycle === 'yearly' && (
                <p className="text-xs font-medium text-teal-700 mt-0.5" data-testid={`subscription-saving-${p.key}`}>
                  Saves {money(net(p.price_monthly) * 12 - net(p.price_yearly))} vs monthly
                </p>
              )}
              <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
              <ul className="mt-3 space-y-1 flex-1">
                {p.features.slice(0, 8).map((f) => (
                  <li key={f} className="flex gap-2 text-xs text-slate-600">
                    <Check size={13} className="text-teal-600 shrink-0 mt-0.5" />
                    {moduleInfo(f)?.label || f.replace(/_/g, ' ')}
                  </li>
                ))}
                {p.features.length > 8 && (
                  <li className="text-xs text-slate-400 pl-5">
                    + {p.features.length - 8} more
                  </li>
                )}
              </ul>
              <button
                onClick={() => choose(p.key)}
                disabled={Boolean(busyPlan) || isCurrent || acc.state === 'active'}
                className={`mt-4 w-full inline-flex items-center justify-center gap-2 font-semibold py-2.5 rounded-xl transition-colors ${isCurrent ? 'bg-slate-100 text-slate-600 border border-slate-300' : 'bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-500 text-white'}`}
                data-testid={`subscription-choose-${p.key}`}
              >
                {busyPlan === p.key ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                {isCurrent ? 'Current plan' : `Switch to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 text-xs text-slate-500">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <p>
          No payment or auto-debit during your free trial — switch plans as often as you like.
          When the trial ends, Unisol sends you a payment link for your plan by WhatsApp or email.
          To change a paid plan, contact Unisol.
        </p>
      </div>

      {(state.payments || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="subscription-payments">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold">Payment history</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-5 py-2">Date</th><th className="text-left px-5 py-2">Plan</th>
                  <th className="text-right px-5 py-2">Amount</th><th className="text-left px-5 py-2">Status</th></tr>
            </thead>
            <tbody>
              {state.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-5 py-2">{String(p.created_at || '').slice(0, 10)}</td>
                  <td className="px-5 py-2 capitalize">{p.plan} · {p.billing_cycle}</td>
                  <td className="px-5 py-2 text-right tabular-nums">{money(p.amount)}</td>
                  <td className="px-5 py-2 capitalize">{p.payment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
